import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { FrameConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/frame/frame-converter';
import { SourceType, UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import { DataModelChange, IDrawingShapeData, DeltaOf, ICTTransform2D } from '@tencent/slide-sdk-model';
import { checkChildrenNeedChangeWidthHeight } from '@src/slide/infrastructure/module/independent/graphic';
import AdjustGroupChildrenBg from '@src/slide/domain/ui-model/element-modeles/property/fill/adjust-group-children-bg';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';

/**
 * 这里只提供各种元素都支持的property的转换
 */
export class ElementCommonPropertyConverter {
    /**
     * 数据3.0转换接口
     * @param sdkData
     * @param uiData
     * @param changesetInfo
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IDrawingShapeData>>,
        uiData: IElementBaseModel,
        changesetInfo?: DataModelChange | null,
    ): void {
        // frame
        ElementCommonPropertyConverter.convertFrameInfoFromSdkToUi(sdkData, uiData);

        // 是否为用户编辑过的占位符
        ElementCommonPropertyConverter.convertIsCustomPlaceholder(sdkData, uiData);

        // 标记图形的源头(slide/master/layout)
        ElementCommonPropertyConverter.convertSourceType(sdkData, uiData);

        // 选择窗格时设置的是否隐藏
        ElementCommonPropertyConverter.convertHidden(sdkData, uiData);

        // 组合子元素适配背景填充
        new AdjustGroupChildrenBg().adjustChildOffsetFrame(uiData, sdkData, changesetInfo);
    }

    // TODO：complexity perf
    public static convertFrameInfoFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IDrawingShapeData>>,
        uiData: IElementBaseModel,
    ): void {
        if (sdkData?.spPr?.xfrm) {
            const uiFrame = this.getFrameInfoFromSdkToUi(sdkData.spPr.xfrm, uiData);
            uiData.props = uiData.props || {};
            uiData.props.style = uiData.props.style || {};
            Object.assign(uiData.props.style, uiFrame);
        }
    }

    /**
     * 纯函数，根据 sdk xfrm 数据获得对应的 ui frame 数据
     */
    public static getFrameInfoFromSdkToUi(
        xfrm: ICTTransform2D,
        uiData: IElementBaseModel,
    ): IFrame {
        const isInGroup = uiData.parent?.type === UIModelType.GROUP;
        const isInSmartArt = uiData.parent?.type === UIModelType.SMART_ART;

        let relativeScale;
        let left = 0;
        let top = 0;
        if (isInGroup) {
            relativeScale = (uiData.parent as IGroupModel).getFrame().getScaleRelativeToSlide();
            left = Number((uiData.parent as IGroupModel).props?.childFrame?.left);
            top = Number((uiData.parent as IGroupModel).props?.childFrame?.top);
        }
        if (isInSmartArt) {
            relativeScale = (uiData.parent as ISmartArtModel).getFrame().getScaleRelativeToSlide();
            // left = Number((uiData.parent as ISmartArtModel).props?.childFrame?.left);
            // top = Number((uiData.parent as ISmartArtModel).props?.childFrame?.top);
        }
        const relativeInfo = {
            scaleX: relativeScale?.scaleX || 1.0,
            scaleY: relativeScale?.scaleY || 1.0,
            left,
            top,
        };

        const uiFrame = {};
        FrameConverter.convertPartialDataFromSdkToUi(
            xfrm,
            uiFrame as IFrame,
            relativeInfo,
            isInGroup || isInSmartArt,
        );
        return uiFrame  as IFrame;
    }

    // eslint-disable-next-line complexity
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IElementBaseModel>,
        toSdkData: DeltaOf<IDrawingShapeData>,
        rawUiData: IElementBaseModel,
    ): void {
        const frameKeys = ['left', 'top', 'width', 'height', 'rotate', 'flipH', 'flipV'];
        const style = uiData.props?.style;
        if (!style) return;
        let frameChanged = false;
        for (const frameKey of frameKeys) {
            if (style[frameKey] !== undefined) {
                frameChanged = true;
                break;
            }
        }
        if (frameChanged) {
            if (!toSdkData.spPr) {
                toSdkData.spPr = {};
            }
            const isInGroup = rawUiData.parent?.type === UIModelType.GROUP;
            let relativeScale;
            let relativeInfo = { scaleX: 1.0, scaleY: 1.0, left: 0, top: 0 };
            let needRotate09Angle = false;
            if (isInGroup) {
                const parent = rawUiData.parent as IGroupModel;
                const parentChildFrame = parent?.groupChildFrameChangedByChildren;
                relativeScale = parent.getFrame().getScaleRelativeToSlide();
                relativeInfo = {
                    scaleX: relativeScale?.scaleX || 1.0,
                    scaleY: relativeScale?.scaleY || 1.0,
                    left: Number(((parentChildFrame ?? parent.props.childFrame)?.left) ?? 0),
                    top: Number(((parentChildFrame ?? parent.props.childFrame)?.top) ?? 0),
                };
                const rawStyle = rawUiData.props.style;
                needRotate09Angle = checkChildrenNeedChangeWidthHeight(
                    this.getCurrentProperty(style, rawStyle, 'rotate'),
                    this.getCurrentProperty(style, rawStyle, 'flipH'),
                    this.getCurrentProperty(style, rawStyle, 'flipV'),
                );
            }
            toSdkData.spPr.xfrm = FrameConverter
                .convertPartialDataFromUiToSdk(style, relativeInfo, needRotate09Angle);
        }
    }

    /**
     * 标记图形的源头(slide/master/layout)
     * @param sdkData
     * @param uiData
     * @private
     */
    private static convertSourceType(
        sdkData: DeepReadonly<DeepPartial<IDrawingShapeData>>,
        uiData: IElementBaseModel,
    ): void {
        const sourceMap = {
            [UIModelType.SLIDE]: SourceType.SLIDE,
            [UIModelType.MASTER]: SourceType.MASTER,
            [UIModelType.LAYOUT]: SourceType.LAYOUT,
        };

        // 组合子元素这种，直接继承父节点的sourceType即可
        if ((uiData.parent as IElementBaseModel)?.sourceType) {
            uiData.sourceType = (uiData.parent as IElementBaseModel).sourceType;
            return;
        }
        // 父节点是slide、master、layout的话，将sourceType赋值
        if (uiData.parent?.type && sourceMap[uiData.parent.type]) {
            uiData.sourceType = sourceMap[uiData.parent.type];
        }
    }

    /**
     * 选择窗格时设置的是否隐藏
     * @param sdkData
     * @param uiData
     * @private
     */
    private static convertHidden(
        sdkData: DeepReadonly<DeepPartial<IDrawingShapeData>>,
        uiData: IElementBaseModel,
    ): void {
        if (sdkData.nvPr?.hidden) {
            uiData.hidden = 1;
        } else {
            uiData.hidden = 0;
        }
    }

    /**
     * 是否为用户编辑过的占位符
     * @param sdkData
     * @param uiData
     * @private
     */
    private static convertIsCustomPlaceholder(
        sdkData: DeepReadonly<DeepPartial<IDrawingShapeData>>,
        uiData: IElementBaseModel,
    ): void {
        if (sdkData.isCustomPlaceholder === true || sdkData.isCustomPlaceholder === false) {
            uiData.isCustomPlaceholder = sdkData.isCustomPlaceholder;
        }
    }

    private static getCurrentProperty(uiData: any, rawUiData: any, key: string): any {
        if (uiData?.[key] !== undefined) return uiData[key];
        if (rawUiData?.[key] !== undefined) return rawUiData[key];
        return undefined;
    }
}
